import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { LeafQForm } from "@/components/health/LeafQForm";

/**
 * /health/red-s — RED-S protection layer (BW18 PR-H).
 *
 * EA trend chart, monthly LEAF-Q form, screen history, fueling-pattern
 * alert log. Bone health prompt logic (annual DEXA reminder) hooks into
 * the suggestion engine.
 */
export function HealthRedS() {
  return (
    <article aria-labelledby="reds-heading">
      <p
        style={{
          fontFamily: "var(--font-sans)",
          fontSize: "var(--type-micro)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: "var(--fg-3)",
          margin: 0,
        }}
      >
        <Link to="/health" style={{ color: "var(--fg-3)" }}>
          Health
        </Link>{" "}
        · RED-S protection
      </p>
      <h1
        id="reds-heading"
        style={{
          fontFamily: "var(--font-serif)",
          fontStyle: "italic",
          fontWeight: 500,
          fontSize: "var(--type-display-m)",
          margin: "var(--space-2) 0 var(--space-8)",
        }}
      >
        Energy availability matters.
      </h1>

      <div style={{ display: "grid", gap: "var(--space-6)" }}>
        <EaTrendChart />
        <LeafQForm />
        <ScreenHistory />
      </div>
    </article>
  );
}

function EaTrendChart() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["ea_trend_60d", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return [];
      const since = new Date();
      since.setDate(since.getDate() - 60);
      const { data } = await supabase
        .from("daily_recovery")
        .select("date, ea_kcal_per_kg_ffm, ea_status")
        .eq("user_id", userId)
        .gte("date", since.toISOString().slice(0, 10))
        .order("date", { ascending: true });
      return data ?? [];
    },
  });

  const data = (q.data ?? []).filter((d) => d.ea_kcal_per_kg_ffm != null).map((d) => ({
    date: d.date.slice(5),
    ea: Number(d.ea_kcal_per_kg_ffm),
  }));

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-surface)",
        padding: "var(--space-5) var(--space-6)",
      }}
    >
      <h3 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
        Energy availability, last 60 days
      </h3>
      <p
        style={{
          margin: "var(--space-2) 0 var(--space-4)",
          color: "var(--fg-2)",
          fontSize: "var(--type-body-s)",
          lineHeight: 1.5,
        }}
      >
        EA = (kcal in − kcal workout) ÷ kg fat-free mass. IOC consensus:
        ≥45 optimal, 30-45 adequate, 25-30 risk, &lt;25 problematic.
      </p>
      {data.length === 0 ? (
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-body-s)" }}>
          EA history will appear once Recovery has computed for several days.
        </p>
      ) : (
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: "var(--fg-3)" }} />
              <YAxis tick={{ fontSize: 11, fill: "var(--fg-3)" }} />
              <Tooltip
                contentStyle={{
                  background: "var(--bg-2)",
                  border: "1px solid var(--hair)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <ReferenceLine y={45} stroke="var(--accent-dim)" strokeDasharray="2 4" label={{ value: "optimal", fontSize: 10, fill: "var(--fg-3)" }} />
              <ReferenceLine y={30} stroke="var(--accent-dim)" strokeDasharray="2 4" label={{ value: "adequate floor", fontSize: 10, fill: "var(--fg-3)" }} />
              <ReferenceLine y={25} stroke="var(--brick)" strokeDasharray="2 4" label={{ value: "RED-S risk", fontSize: 10, fill: "var(--brick)" }} />
              <Line type="monotone" dataKey="ea" stroke="var(--accent)" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}
    </section>
  );
}

function ScreenHistory() {
  const { session } = useSession();
  const userId = session?.user?.id;

  const q = useQuery({
    queryKey: ["leaf_q_history", userId],
    enabled: Boolean(userId),
    queryFn: async () => {
      if (!userId) return [];
      const { data } = await supabase
        .from("leaf_q_response")
        .select("date, score, symptoms_count, risk_level")
        .eq("user_id", userId)
        .order("date", { ascending: false })
        .limit(12);
      return data ?? [];
    },
  });

  const rows = q.data ?? [];

  return (
    <section
      style={{
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-surface)",
        padding: "var(--space-5) var(--space-6)",
      }}
    >
      <h3 style={{ margin: 0, fontSize: "var(--type-body-l)", fontWeight: 500 }}>
        Screen history
      </h3>
      {rows.length === 0 ? (
        <p
          style={{
            margin: "var(--space-3) 0 0",
            color: "var(--fg-3)",
            fontSize: "var(--type-body-s)",
          }}
        >
          No screens yet. Submit one above to start tracking.
        </p>
      ) : (
        <table
          style={{
            marginTop: "var(--space-3)",
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "var(--type-body-s)",
          }}
        >
          <thead>
            <tr style={{ color: "var(--fg-3)", textAlign: "left" }}>
              <th style={cellStyle()}>Date</th>
              <th style={cellStyle()}>Score</th>
              <th style={cellStyle()}>Symptoms</th>
              <th style={cellStyle()}>Risk</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.date} style={{ borderTop: "1px solid var(--hair-2)" }}>
                <td style={cellStyle()}>{r.date}</td>
                <td style={cellStyle()}>{r.score}</td>
                <td style={cellStyle()}>{r.symptoms_count}</td>
                <td style={cellStyle()}>
                  <span
                    style={{
                      color:
                        r.risk_level === "high"
                          ? "var(--brick)"
                          : r.risk_level === "moderate"
                            ? "var(--accent)"
                            : "var(--fg-2)",
                    }}
                  >
                    {r.risk_level}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}

function cellStyle(): React.CSSProperties {
  return { padding: "var(--space-2) var(--space-3)" };
}
